import { isEmptyString } from '@/utils/string';
import { isUnitedStates, isZipCodeIgnored } from './address-options';
import type { CreditCardFormAddress } from './credit-card-form-submit';

export type BillingFieldErrors = {
  name?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
};

/** Per-field validation errors for the name + billing address. Mirrors the Ember address-form rules. */
export function billingFieldErrors(name: string, address: CreditCardFormAddress): BillingFieldErrors {
  const errors: BillingFieldErrors = {};

  if (isEmptyString(name)) {
    errors.name = 'Name is required.';
  }
  if (isEmptyString(address.addressLine1)) {
    errors.addressLine1 = "Enter your company's address";
  }
  if (isEmptyString(address.city)) {
    errors.city = "Enter your company's city";
  }
  if (isUnitedStates(address.country) && isEmptyString(address.state)) {
    errors.state = "Enter your company's state";
  }
  if (isEmptyString(address.country)) {
    errors.country = "Enter your company's country";
  }
  if (!isZipCodeIgnored(address.country) && isEmptyString(address.zipCode)) {
    errors.zipCode = "Enter your company's zip code";
  }

  return errors;
}

/** The first field error, in the order the Ember form surfaced them, or undefined when all valid. */
export function firstBillingError(errors: BillingFieldErrors): string | undefined {
  return errors.name ?? errors.addressLine1 ?? errors.city ?? errors.state ?? errors.country ?? errors.zipCode;
}

/** Whether every required billing field is present. */
export function hasRequiredBillingFields(name: string, address: CreditCardFormAddress): boolean {
  return firstBillingError(billingFieldErrors(name, address)) == null;
}

type SubmitStateInput = {
  canEdit: boolean;
  masqReadOnly: boolean;
  isVerificationRejected: boolean;
  hasRequiredFields: boolean;
  isCardComplete: boolean;
  isSubmitting: boolean;
};

/** Whether the submit button is disabled. Card completeness is bypassed in test mode by the caller. */
export function isSubmitDisabled({
  canEdit,
  masqReadOnly,
  isVerificationRejected,
  hasRequiredFields,
  isCardComplete,
  isSubmitting,
}: SubmitStateInput): boolean {
  return [!canEdit, masqReadOnly, isVerificationRejected, !hasRequiredFields, !isCardComplete, isSubmitting].some(
    Boolean,
  );
}

/**
 * Why submit is blocked while a tax-rate lookup is in flight. Submit is held from the first keystroke
 * in an address field until the new rate lands, so without this the button is disabled with nothing to
 * explain it for the whole debounce plus request.
 */
export const TAX_REFRESH_TOOLTIP = 'Updating the tax for this address.';

/**
 * The tooltip shown over a disabled submit button, or empty string when there's nothing to explain.
 * Prioritizes verification-rejected, then the specific first field error, then incomplete card.
 *
 * The tax message comes last, and so appears only when the form is otherwise submittable: it is the
 * one block the user cannot act on and that clears by itself, so anything they must fix wins over it.
 */
export function submitTooltipMessage({
  isVerificationRejected,
  verificationRejectedMessage,
  firstFieldError,
  isCardComplete,
  isTaxRateLoading = false,
}: {
  isVerificationRejected: boolean;
  verificationRejectedMessage: string;
  firstFieldError: string | undefined;
  isCardComplete: boolean;
  /** Whether a tax-rate lookup is in flight. Only checkout refreshes tax, so it defaults to false. */
  isTaxRateLoading?: boolean;
}): string {
  if (isVerificationRejected) {
    return verificationRejectedMessage;
  }
  if (firstFieldError != null) {
    return firstFieldError;
  }
  if (!isCardComplete) {
    return 'Your card details are incomplete.';
  }
  if (isTaxRateLoading) {
    return TAX_REFRESH_TOOLTIP;
  }
  return '';
}
